import { Category, LocalizedString } from '@synergy/models';

/** The category the distillate suppliers keep their delivery regions under (#2322 R-226). */
export const DISTILLATE_GROUP_NAME = 'Destillate';

/** Where the goods of one customer card go, as the supplier search takes it. */
export interface InquiryDeliveryRegion {
    region: string;
    zip: string;
}

/**
 * The categories of one position no other of its categories hangs below.
 *
 * A product is filed under every level of its branch — Tankstellen & Lager ›
 * Destillate › Winterdiesel — and offered all three, the filter looked for the
 * whole of Tankstellen & Lager (#2326 R-252).
 */
export const deepestCategoriesOf = (categories: Category[]): Category[] => {
    const parents = new Set(categories.map(category => category.parentId).filter(Boolean));

    return categories.filter(category => !parents.has(category.id));
};

/**
 * Per position, the deepest category some supplier carries: Winterdiesel has
 * none, so the filter starts with Destillate. A branch nobody carries keeps its
 * deepest category, which at least names the position.
 */
export const startingCategoriesOf = async (
    positions: Category[][],
    hasSuppliers: (categoryId: number) => Promise<boolean>
): Promise<Category[]> => {
    const asked = new Map<number, Promise<boolean>>();
    const ask = (id: number): Promise<boolean> => {
        if (!asked.has(id)) {
            asked.set(id, hasSuppliers(id));
        }
        return asked.get(id) as Promise<boolean>;
    };
    const starting: Category[] = [];

    for (const categories of positions) {
        const byId = new Map(categories.map(category => [category.id, category]));

        for (const deepest of deepestCategoriesOf(categories)) {
            let current: Category | undefined = deepest;

            while (current && !(await ask(current.id))) {
                current = current.parentId ? byId.get(current.parentId) : undefined;
            }

            const chosen = current ?? deepest;

            if (!starting.some(category => category.id === chosen.id)) {
                starting.push(chosen);
            }
        }
    }

    return starting;
};

/**
 * "Passt zu" counts positions, not categories: a supplier of Destillate covers
 * the Winterdiesel position in full, though it is not filed under Winterdiesel
 * (#2326 R-252). Only positions the filter still asks for count; a filter that
 * asks for none of them counts its own groups, as before.
 */
export const coverageOf = (
    supplierCategoryIds: number[],
    positions: number[][],
    selectedGroups: number[][]
): { covered: number; of: number } => {
    const selected = selectedGroups.flat();
    const asked = positions
        .map(position => position.filter(id => selected.includes(id)))
        .filter(position => position.length > 0);
    const units = asked.length > 0 ? asked : selectedGroups;

    return {
        covered: units.filter(ids => ids.some(id => supplierCategoryIds.includes(id))).length,
        of: units.length,
    };
};

/** Whether one of the ids is Destillate or lies below it. */
export const asksForDistillates = (
    ids: number[],
    known: Category[],
    nameOf: (value?: LocalizedString) => string
): boolean => {
    const byId = new Map(known.map(category => [category.id, category]));

    return ids.some(id => {
        for (let current = byId.get(id); current; current = current.parentId ? byId.get(current.parentId) : undefined) {
            if (DISTILLATE_GROUP_NAME === nameOf(current.translations).trim()) {
                return true;
            }
        }
        return false;
    });
};

/** The region of a delivery address arrives as its name, or as name and code. */
const regionNameOf = (region: unknown): string => {
    if (typeof region === 'string') {
        return region.trim();
    }
    if (region && typeof region === 'object' && 'name' in region) {
        return String((region as { name?: unknown }).name ?? '').trim();
    }
    return '';
};

/** The delivery regions of the customer cards, each once. */
export const deliveryRegionsOf = (
    addresses: ({ region?: unknown; zip?: string } | undefined)[]
): InquiryDeliveryRegion[] => {
    const regions: InquiryDeliveryRegion[] = [];

    addresses.forEach(address => {
        const region = regionNameOf(address?.region);
        const zip = (address?.zip ?? '').trim();

        if (region && !regions.some(known => known.region === region && known.zip === zip)) {
            regions.push({ region, zip });
        }
    });

    return regions;
};

/** `region|zip`, comma-separated, as the supplier search reads it. */
export const deliveryRegionsParam = (regions: InquiryDeliveryRegion[]): string =>
    regions.map(({ region, zip }) => `${region.replace(/[|,]/g, ' ')}|${zip.replace(/[|,]/g, ' ')}`).join(',');
